<ph-dialog [open]="open"
           brand
           width="440"
           icon="shield-check"
           [heading]="heading"
           [description]="subtitle"
           [noClose]="disableClose || !!replacementCode"
           (ph-close)="onDialogClose()">

    <ng-container *ngIf="open">
        <!-- SD-11060: a redeemed code is replaced; show the new one once. One code needs only Copy, so this is
             built from the dialog's own kit parts rather than the full-set recovery-codes panel. -->
        <ph-stack *ngIf="replacementCode">
            <ph-alert status="information">This is the only time this code will be shown.</ph-alert>

            <ph-toolbar nowrap>
                <ph-input size="large">
                    <input slot="control"
                           type="text"
                           readonly
                           e2eId="confirm-2fa-replacement-code"
                           aria-label="New recovery code"
                           [value]="replacementCode">
                </ph-input>
                <ph-button variant="secondary"
                           mode="filled"
                           size="large"
                           icon="copy"
                           e2eId="confirm-2fa-replacement-copy"
                           [copyToClipboard]="replacementCode"
                           copyConfirm="inline"
                           (copyStateChange)="replacementCopyState = $event">{{ replacementCopyState === 'copied' ? 'Copied' : 'Copy' }}</ph-button>
            </ph-toolbar>

            <ph-field inline align="start" boxed>
                <ph-checkbox id="confirm-2fa-replacement-saved"
                             e2eId="confirm-2fa-replacement-saved"
                             aria-label="I have saved this code"
                             [checked]="replacementSaved"
                             (ph-change)="replacementSaved = $event.detail"></ph-checkbox>
                <ph-field>
                    <ph-label for="confirm-2fa-replacement-saved">I have saved this code</ph-label>
                    <ph-hint>Keep it with your other recovery codes. If you printed them, write it on the sheet. It replaces the one you just used.</ph-hint>
                </ph-field>
            </ph-field>

            <ph-button size="large"
                       block
                       trailing-icon="arrow-right"
                       e2eId="confirm-2fa-replacement-continue"
                       [disabled]="!replacementSaved"
                       (click)="continueAfterReplacement()">Continue</ph-button>
        </ph-stack>

        <ph-stack *ngIf="!type && !replacementCode">
            <ph-stack gap="snug">
                <ph-choice-tile *ngIf="canUsePasskey"
                                value="passkey"
                                icon="fingerprint"
                                heading-level="span"
                                heading="Use a passkey"
                                description="Use the passkey saved on this device"
                                e2eId="confirm-2fa-passkey-tile"
                                [disabled]="isSendingSms"
                                (ph-select)="onMethodSelect($event.detail)"></ph-choice-tile>
                <ph-choice-tile *ngIf="canUseSms"
                                value="sms"
                                icon="chat-circle-text"
                                heading-level="span"
                                [heading]="isSendingSms ? 'Sending code...' : 'Text me a code'"
                                description="Receive a code via SMS"
                                [disabled]="isSendingSms"
                                (ph-select)="onMethodSelect($event.detail)"></ph-choice-tile>
                <ph-choice-tile *ngIf="canUseGoogle"
                                value="google"
                                icon="device-mobile"
                                heading-level="span"
                                heading="Authenticator app"
                                description="Use your authenticator app"
                                [disabled]="isSendingSms"
                                (ph-select)="onMethodSelect($event.detail)"></ph-choice-tile>
            </ph-stack>

            <ph-stack align="center">
                <ph-link-button variant="gray" (click)="cancel()">Cancel</ph-link-button>
            </ph-stack>
        </ph-stack>

        <ph-stack *ngIf="type === 'passkey'">
            <ph-auth-status icon="fingerprint" [attr.busy]="isPasskeyBusy || isLoadingPasskey ? '' : null">
                <ph-auth-heading level="2" size="caption" align="center">{{ passkeyStatus }}</ph-auth-heading>
            </ph-auth-status>

            <ph-stack *ngIf="passkeyError" align="center">
                <ph-hint [hasError]="true" e2eId="confirm-2fa-passkey-error">{{ passkeyError }}</ph-hint>
            </ph-stack>

            <ph-field *ngIf="showTrustDevice" inline align="start" boxed>
                <ph-checkbox id="confirm-2fa-trust-device-passkey"
                             aria-label="Trust this browser"
                             [checked]="trustDevice"
                             (ph-change)="trustDevice = $event.detail"></ph-checkbox>
                <ph-field>
                    <ph-label for="confirm-2fa-trust-device-passkey">Trust this browser</ph-label>
                    <ph-hint>Don't ask for a code again on this browser for 30 days. Only use on personal devices.</ph-hint>
                </ph-field>
            </ph-field>

            <ph-button size="large"
                       block
                       trailing-icon="arrow-right"
                       e2eId="confirm-2fa-passkey-submit"
                       [disabled]="!passkeyOptions || isPasskeyBusy || isSuccess"
                       (click)="usePasskey()">{{ passkeyButtonLabel }}</ph-button>

            <ph-toolbar>
                <ph-link-button variant="gray" icon="arrow-left" (click)="goBack()">Choose another method</ph-link-button>
            </ph-toolbar>
        </ph-stack>

        <ph-stack *ngIf="type && type !== 'recovery' && type !== 'passkey' && !replacementCode">
            <ph-stack gap="snug" align="center">
                <ph-digit-input #digits
                                digits="6"
                                aria-label="Verification code"
                                auto-focus
                                [value]="code"
                                [disabled]="isSubmitting || isSuccess"
                                [hasError]="hasError"
                                (ph-change)="onCodeChange($event.detail)"
                                (ph-complete)="submit($event.detail)"></ph-digit-input>

                <ph-hint [hasError]="hasError">{{ hintText }}</ph-hint>
            </ph-stack>

            <ph-stack *ngIf="showResendRow" gap="tight" align="center">
                <ph-hint [hasError]="isExpired || smsSendFailed">{{ resendStatus }}</ph-hint>
                <ph-toolbar justify="center" gap="tight" nowrap>
                    <ph-hint>Didn't get a code?</ph-hint>
                    <ph-link-button variant="primary"
                                    size="small"
                                    e2eId="confirm-2fa-resend"
                                    [disabled]="isResending || isSubmitting || isSuccess"
                                    (click)="resendCode()">{{ isResending ? 'Sending…' : 'Resend code' }}</ph-link-button>
                </ph-toolbar>
            </ph-stack>

            <ph-field *ngIf="showTrustDevice" inline align="start" boxed>
                <ph-checkbox id="confirm-2fa-trust-device"
                             e2eId="confirm-2fa-trust-device"
                             aria-label="Trust this browser"
                             [checked]="trustDevice"
                             (ph-change)="trustDevice = $event.detail"></ph-checkbox>
                <ph-field>
                    <ph-label for="confirm-2fa-trust-device">Trust this browser</ph-label>
                    <ph-hint>Don't ask for a code again on this browser for 30 days. Only use on personal devices.</ph-hint>
                </ph-field>
            </ph-field>

            <ph-button size="large"
                       block
                       trailing-icon="arrow-right"
                       e2eId="confirm-2fa-submit"
                       [disabled]="code.length < 6 || isSubmitting || isSuccess"
                       (click)="submit(code)">{{ verifyLabel }}</ph-button>

            <ph-toolbar justify="between">
                <ph-link-button *ngIf="canGoBack" variant="gray" icon="arrow-left" (click)="goBack()">Back</ph-link-button>
                <ph-link-button *ngIf="!canGoBack" variant="gray" (click)="cancel()">Cancel</ph-link-button>
                <ph-link-button *ngIf="canUseRecoveryCode"
                                variant="primary"
                                e2eId="confirm-2fa-use-recovery"
                                [disabled]="isSubmitting || isSuccess"
                                (click)="setRecoveryType()">Use a recovery code instead</ph-link-button>
            </ph-toolbar>
        </ph-stack>

        <ph-stack *ngIf="type === 'recovery' && !replacementCode">
            <ph-stack gap="snug">
                <ph-input size="large" [hasError]="hasError">
                    <input slot="control"
                           type="text"
                           [mask]="recoveryCodeMask"
                           e2eId="confirm-2fa-recovery-input"
                           aria-label="Recovery code"
                           placeholder="XXXXX-XXXX-XXXX"
                           autocomplete="one-time-code"
                           [(ngModel)]="recoveryCode"
                           [disabled]="isSubmitting || isSuccess"
                           (keyup.enter)="submitRecovery()" />
                </ph-input>

                <ph-stack align="center">
                    <ph-hint [hasError]="hasError">{{ recoveryHint }}</ph-hint>
                </ph-stack>
            </ph-stack>

            <ph-field *ngIf="showTrustDevice" inline align="start" boxed>
                <ph-checkbox id="confirm-2fa-trust-device-recovery"
                             aria-label="Trust this browser"
                             [checked]="trustDevice"
                             (ph-change)="trustDevice = $event.detail"></ph-checkbox>
                <ph-field>
                    <ph-label for="confirm-2fa-trust-device-recovery">Trust this browser</ph-label>
                    <ph-hint>Don't ask for a code again on this browser for 30 days. Only use on personal devices.</ph-hint>
                </ph-field>
            </ph-field>

            <ph-button size="large"
                       block
                       trailing-icon="arrow-right"
                       e2eId="confirm-2fa-recovery-submit"
                       [disabled]="recoveryCode.trim().length < 10 || isSubmitting || isSuccess"
                       (click)="submitRecovery()">{{ verifyLabel }}</ph-button>

            <ph-toolbar>
                <ph-link-button variant="gray" icon="arrow-left" (click)="backFromRecovery()">Back</ph-link-button>
            </ph-toolbar>
        </ph-stack>
    </ng-container>
</ph-dialog>
